import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useField } from 'react-final-form';

import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { TextInput } from 'UIPackage/MainModule/components/TextInput';

import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { IKnmFormRef } from '../interfaces/IKnmFormRef';
import {
  getKnmDataCreated,
  getKnmIsErknmStatusTypeCarriedOut,
  getKnmIsErknmStatusTypeCompleted,
} from '../selectors/knmSelectors';
import { IReasonRequirements } from '../../../APIPackage/DictionariesModule/interfaces/IReasonRequirements';
import { CHECKING_IN_REGESTRY, CHECKING_IN_REGESTRY_ORDER } from '../../../CorePackage/MainModule/constants/notices';
import { MspCodesEnum } from '../../../APIPackage/KnmModule/enums/MspCodesEnum';
import { IInspectionResult } from '../../../APIPackage/CatalogsModule/interfaces/IInspectionResult';
import { IKnmCheckSubjectResult } from '../../../APIPackage/KnmModule/interfaces/IKnmCheckSubjectResult';
import { convertInitialKnmDateToFormDate } from '../common/convertInitialKnmDateToFormDate';
import { DATE_FORMAT_LONG_FNS } from '../../../CorePackage/MainModule/constants/dateFormats';
import { useFnsRegistrySearchMsp } from '../hooks/useFnsRegistrySearch';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { mspCategoryTitlesByMspCodes } from '../constants/mspCategoryTitlesByMspCodes';
import { FnsCheckStatusesEnum } from '../../../KnmErknmPackage/ErknmSubjectModule/enums/FnsCheckStatusesEnum';
import { FnsRegistryCheckMessageEnum } from '../enums/FnsRegistryCheckMessageEnum';
import styles from './KnmMspField.module.scss';

interface IKnmMspFieldProps {
  checkSubjectResult?: IKnmCheckSubjectResult | null;
  ervkId: string | undefined;
  id?: string;
  inn?: string;
  inspectionResult?: IInspectionResult;
  isShowMessages?: boolean;
  formRef: IKnmFormRef | undefined;
  mspRegistryDate?: string | null;
  name?: string;
  value?: string | MspCodesEnum;
}

interface IKnmMspFieldStoreProps {
  created: string | null | undefined;
  isErknmStatusTypeCarriedOut: boolean;
  isErknmStatusTypeCompleted: boolean;
  settings: IAdminSetting[] | undefined;
}

const KnmMspFieldWithoutStore = ({
  created,
  isErknmStatusTypeCarriedOut,
  isErknmStatusTypeCompleted,
  settings,
  checkSubjectResult,
  ervkId,
  id,
  inn,
  inspectionResult,
  isShowMessages,
  formRef,
  mspRegistryDate,
  name,
  value,
}: IKnmMspFieldProps & IKnmMspFieldStoreProps) => {
  const {
    input: { value: orderRequirements },
  } = useField('orderRequirements');
  const isExistInMsp: string = useMemo(() => {
    if (!value) {
      return '';
    }

    return value === MspCodesEnum.NON_EXIST ? 'Нет' : 'Да';
  }, [value]);

  const [isErrorValidationMspCompany, setIsErrorValidationMspCompany] = useState('');
  const [fnsCheckDateWarningMessage, setFnsCheckDateWarningMessage] = useState<string | undefined>(undefined);
  const [fnsCheckDateSuccessMessage, setFnsCheckDateSuccessMessage] = useState<string | undefined>(undefined);
  const { getFnsRegistryDateMsp } = useFnsRegistrySearchMsp();

  const isShowCheckResultMessage = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.SHOW_CHECK_RESULT_MESSAGE_523_4_1_17,
  );

  const isShowMessage5234153 = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_523_4_1_53_STARTS_FROM,
  );

  const isShowMessage4154 = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_54_STARTS_FROM,
  );

  const fetchFnsRegistry = useCallback(
    async (innValue: string) => {
      const getFnsRegistryDateByMspResponse = await getFnsRegistryDateMsp(innValue);

      if (getFnsRegistryDateByMspResponse?.success && getFnsRegistryDateByMspResponse?.data) {
        if (name && !ervkId && checkSubjectResult?.msp?.status !== FnsCheckStatusesEnum.FOUND) {
          if (!value) {
            // @ts-ignore
            formRef?.current.change(name, getFnsRegistryDateByMspResponse.data.mspCategory);
          }
        }

        setFnsCheckDateWarningMessage(
          `Дата получения выписки ФНС ${convertInitialKnmDateToFormDate(
            getFnsRegistryDateByMspResponse.data.mspRegistryDate,
            DATE_FORMAT_LONG_FNS,
          )}. ${FnsRegistryCheckMessageEnum.PLEASE_SAVE_KNM}`,
        );
      } else {
        setFnsCheckDateWarningMessage(undefined);
      }
    },
    [getFnsRegistryDateMsp],
  );

  /**
   * Ошибка валидации МСП
   */
  useEffect(() => {
    const reasonRequirements: IReasonRequirements | undefined = formRef?.current.getFieldState(`reasonRequirements`)
      ?.value;

    if (orderRequirements && isShowMessage5234153) {
      if (orderRequirements.flagMsp !== null) {
        if (
          value !== undefined &&
          ((orderRequirements.flagMsp === 'true' && value === MspCodesEnum.NON_EXIST) ||
            (orderRequirements.flagMsp === 'false' && value !== MspCodesEnum.NON_EXIST))
        ) {
          setIsErrorValidationMspCompany(CHECKING_IN_REGESTRY_ORDER);
        } else {
          setIsErrorValidationMspCompany('');
        }
      }
    } else if (reasonRequirements && reasonRequirements.msp !== null && isShowMessage4154) {
      if (
        value !== undefined &&
        ((reasonRequirements.msp && value === MspCodesEnum.NON_EXIST) ||
          (!reasonRequirements?.msp && value !== MspCodesEnum.NON_EXIST))
      ) {
        setIsErrorValidationMspCompany(CHECKING_IN_REGESTRY);
      } else {
        setIsErrorValidationMspCompany('');
      }
    } else {
      setIsErrorValidationMspCompany('');
    }
  }, [formRef?.current, value, orderRequirements]);

  useEffect(() => {
    if (!ervkId && isShowCheckResultMessage) {
      if (
        checkSubjectResult?.msp?.status === FnsCheckStatusesEnum.FOUND ||
        checkSubjectResult?.msp?.status === FnsCheckStatusesEnum.NO_DATA_MSP
      ) {
        setFnsCheckDateSuccessMessage(
          `Дата получения выписки ФНС ${convertInitialKnmDateToFormDate(
            checkSubjectResult.msp.time,
            DATE_FORMAT_LONG_FNS,
          )}`,
        );
      }
      // Если паспорт в статусе "Ожидает завершения" или "Завершено" - актуализация данных по МСП уже не требуется
      else if (!isErknmStatusTypeCarriedOut && !isErknmStatusTypeCompleted) {
        setFnsCheckDateSuccessMessage(undefined);
        if (inn) {
          fetchFnsRegistry(inn);
        } else {
          setFnsCheckDateWarningMessage(undefined);
        }
      }
    } else {
      setFnsCheckDateWarningMessage(undefined);
      setFnsCheckDateSuccessMessage(undefined);
    }
  }, [checkSubjectResult, value, ervkId, inn, fetchFnsRegistry]);

  const warning = () => {
    if (!isShowCheckResultMessage || !isShowMessages || ervkId) {
      return undefined;
    }
    if (checkSubjectResult?.msp && checkSubjectResult?.msp?.status !== FnsCheckStatusesEnum.IN_PROGRESS) {
      return undefined;
    }
    if (!value && inn) {
      return FnsRegistryCheckMessageEnum.PLEASE_SAVE_KNM;
    }
    if (mspRegistryDate) {
      return `Дата получения выписки ФНС ${mspRegistryDate}. ${FnsRegistryCheckMessageEnum.PLEASE_SAVE_KNM}`;
    }
    if (fnsCheckDateWarningMessage) {
      return fnsCheckDateWarningMessage;
    }

    return undefined;
  };

  return (
    <>
      <FieldBlock
        label="Наличие проверяемого лица в реестре МСП"
        placementLabel="left"
        margin="top"
        id={id}
        warning={warning()}
        error={inspectionResult ? undefined : isErrorValidationMspCompany}
        success={isShowMessages ? fnsCheckDateSuccessMessage : undefined}
      >
        <TextInput className="shared-row-field" value={isExistInMsp} disabled inspectionResult={inspectionResult} />
      </FieldBlock>
      <FieldBlock
        label="Категория субъекта МСП"
        placementLabel="left"
        margin="top"
        id={id}
        warning={warning()}
        success={isShowMessages ? fnsCheckDateSuccessMessage : undefined}
      >
        <p className={styles.mspCodeTitle}>{mspCategoryTitlesByMspCodes[value as MspCodesEnum]}</p>
      </FieldBlock>
    </>
  );
};

const mapStateToProps = (state: IRootState): IKnmMspFieldStoreProps => ({
  created: getKnmDataCreated(state),
  isErknmStatusTypeCarriedOut: getKnmIsErknmStatusTypeCarriedOut(state),
  isErknmStatusTypeCompleted: getKnmIsErknmStatusTypeCompleted(state),
  settings: getAdminFetchSettings(state),
});

export const KnmMspField = connect(mapStateToProps)(KnmMspFieldWithoutStore);
